/**
 * Model dopravní špičky a presety časového posuvníku.
 *
 * Špička: jednoduchý, transparentně označený model — v konfigurovatelných
 * oknech (ráno/odpoledne) se jízda autem prodlužuje daným faktorem,
 * s plynulým náběhem 30 minut před/po okně (lineární interpolace),
 * aby 14:11 a 14:30 mohly dát odlišný výsledek i na hraně okna.
 * Není to měření reálného provozu — to by vyžadovalo placená traffic API;
 * model je proto viditelně popsán ve vysvětlivce parametru.
 */
import { pragueMinutesOfDay } from './time';

export interface PeakConfig {
  enabled: boolean;
  morningFromH: number;
  morningToH: number;
  morningFactor: number;
  afternoonFromH: number;
  afternoonToH: number;
  afternoonFactor: number;
}

export const DEFAULT_PEAK: PeakConfig = {
  enabled: true,
  morningFromH: 7,
  morningToH: 9,
  morningFactor: 1.35,
  afternoonFromH: 15.5,
  afternoonToH: 18,
  afternoonFactor: 1.4,
};

const RAMP_MIN = 30; // plynulý náběh/doběh špičky

/** Faktor jednoho okna v čase t (minuty dne); 1 mimo okno, factor uvnitř. */
function windowFactor(tMin: number, fromH: number, toH: number, factor: number): number {
  const from = fromH * 60;
  const to = toH * 60;
  if (to <= from) return 1; // nevalidní okno
  if (tMin >= from && tMin <= to) return factor;
  if (tMin >= from - RAMP_MIN && tMin < from) {
    return 1 + (factor - 1) * ((tMin - (from - RAMP_MIN)) / RAMP_MIN);
  }
  if (tMin > to && tMin <= to + RAMP_MIN) {
    return 1 + (factor - 1) * ((to + RAMP_MIN - tMin) / RAMP_MIN);
  }
  return 1;
}

/**
 * Zpomalovací faktor jízdy autem v daném absolutním čase (≥ 1).
 * Čas jízdy se NÁSOBÍ tímto faktorem. O(1).
 */
export function trafficFactorAt(ms: number, cfg: PeakConfig): number {
  if (!cfg.enabled) return 1;
  const t = pragueMinutesOfDay(ms);
  return Math.max(
    windowFactor(t, cfg.morningFromH, cfg.morningToH, cfg.morningFactor),
    windowFactor(t, cfg.afternoonFromH, cfg.afternoonToH, cfg.afternoonFactor),
  );
}

/* ------------------------------------------------------------------ */
/* Presety časového posuvníku                                          */
/* ------------------------------------------------------------------ */

export interface TimePreset {
  id: 'now' | 'morning' | 'noon' | 'afternoon' | 'evening';
  label: string;
  /** Minuty od půlnoci; null = aktuální čas. */
  minutes: number | null;
}

export const TIME_PRESETS: TimePreset[] = [
  { id: 'now', label: 'Teď', minutes: null },
  { id: 'morning', label: 'Ráno', minutes: 7 * 60 + 30 },
  { id: 'noon', label: 'Poledne', minutes: 12 * 60 },
  { id: 'afternoon', label: 'Odpoledne', minutes: 16 * 60 + 30 },
  { id: 'evening', label: 'Večer', minutes: 20 * 60 },
];
